import React, { useEffect, useRef, useState } from 'react'
import { useFindMenu, useIsMobile, useTrans, useComparatore, useTealium } from '@next-nx/hooks'
import { Bin } from '@next-nx/shared-ui/icons'
import {
  WContainer,
  Breadcrumbs,
  MainTitle,
  MainDescription,
  Button,
} from '@next-nx/shared-ui/atoms'
import { ComparatoreCard, ComparatoreCardFull } from '@next-nx/shared-ui/molecules'
import {
  Page,
  ComparatoreTable,
  ComparatoreTableBar,
  VantaggiSection,
  HelpBanner,
  ComparatoreSuggerimenti,
} from '@next-nx/shared-ui/organisms'
import styles from './ComparatoreTemplate.module.sass'
import { HEADER_MAIN_HEIGHT } from '@next-nx/utils/settings'
import { useOfferteBySlugQuery } from '@next-nx/shared-graphql/unipolrental'
import { CategoriaType, OffertaType, PaginaType } from '@next-nx/utils/types'
import { getTimestamp } from '@next-nx/utils'

interface Props {
  categoria: CategoriaType
  pagina: PaginaType
}

const ComparatoreTemplate = (props: Props) => {
  const { categoria, pagina } = props

  const t = useTrans()
  const { compareItems, addItem, resetItems, removeItem } = useComparatore()
  const { linkDataLayer } = useTealium()

  const { data: offerteData, loading } = useOfferteBySlugQuery({
    variables: { slugs: compareItems?.map((o) => o.slug) },
  })

  const [itemsComparatore, setItemsComparatore] = useState<OffertaType[]>([])

  const isMobile = useIsMobile()
  const tableRef = useRef(null)
  const maxItems = isMobile ? 2 : 3
  const [tableBarVisible, setTableBarVisible] = useState(false)

  const handleScroll = () => {
    setTableBarVisible(
      !!tableRef?.current &&
        window.scrollY > tableRef.current.offsetTop - HEADER_MAIN_HEIGHT &&
        window.scrollY <
          tableRef.current.offsetTop - HEADER_MAIN_HEIGHT + tableRef.current.clientHeight - 150 // offset
    )
  }

  const handleTracking = (action_type: string, action_effect: string, cta: string, product?: string) => {
    const action_timestamp = getTimestamp() 
    let dlObj = {}

    switch (action_type) {
      case 'aggiungi_prodotto':
        dlObj = {
          virtual: 'action', 
          action_name: 'comparatore',  
          action_tech: 'click', 
          action_type,
          action_timestamp,
          action_effect,
          cta,
        }
        break;
      case 'configura_veicolo':
        dlObj = {
          virtual: 'action', 
          action_name: 'comparatore',  
          action_tech: 'click', 
          action_type,
          action_timestamp,
          action_effect,
          cta,
          ...(!!product && {product: product.replace(/\/|\-|\s+/g, '_').toLowerCase()})
        }
        break;
      case 'rimuovi':
        dlObj = {
          virtual: 'action', 
          action_name: 'comparatore',  
          action_tech: 'click', 
          action_type,
          action_timestamp,
          action_effect,
          cta,
          ...(!!product && {product: product.replace(/\/|\-|\s+/g, '_').toLowerCase()})
        }
        break;
      case 'azzera_selezione':
        dlObj = {
          virtual: 'action', 
          action_name: 'comparatore',  
          action_tech: 'click', 
          action_type,
          action_timestamp,
          action_effect,
          cta,
        }
        break;
      default:
        break;
    }
    
    linkDataLayer(dlObj) 
  } 

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const comparatoreMenu = useFindMenu('pagina-comparatore')
  const vantaggiMenu = useFindMenu('vantaggi', comparatoreMenu)
  const helpBannerMenu = useFindMenu('help_banner')
  const helpBanner = useFindMenu('listing-pdp', helpBannerMenu)
  const suggeritiMenu = useFindMenu('lasciati_ispirare', comparatoreMenu)

  useEffect(() => {
    // preservo l'ordine perchè la query graphql ritorna le offerte in ordine diverso
    if (offerteData && !loading) {
      const newItemsComparatore = offerteData?.offerteBySlug
        ? compareItems
            .map((ci) => offerteData?.offerteBySlug?.find((o) => o.pk === ci.pk))
            .filter((x) => !!x)
        : []

      if (newItemsComparatore.length !== compareItems.length) {
        resetItems()
      } else {
        setItemsComparatore(
          offerteData?.offerteBySlug
            ? compareItems
                .map((ci) => offerteData?.offerteBySlug?.find((o) => o.pk === ci.pk))
                .filter((x) => !!x)
            : []
        )
      }
    }
  }, [offerteData, loading])

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <ComparatoreTableBar
        items={itemsComparatore}
        showIi
        showIe
        className={`${styles.tableBar} ${tableBarVisible ? styles['tableBar--visible'] : ''}`}
      />
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} className={styles.breadcrumbs} />
        <div className={styles.head}>
          <div className={styles.headMain}>
            <MainTitle label={categoria.nome} className={styles.title} />
            <MainDescription html={categoria.descrizione} className={styles.descrizione} />
          </div>
          <div className={styles.headActions}>
            <Button
              label={t('Azzera selezione')}
              onClick={() => {
                resetItems()
                handleTracking('azzera_selezione', 'azzeramento_selezione_comparatore', 'azzera_selezione_comparatore')
              }}
              variant="ghost"
              icon={<Bin />}
            />
          </div>
        </div>
        <div className={styles.cards}>
          {[...Array(maxItems)].map((x, i) =>
            itemsComparatore.length > i ? (
              <ComparatoreCardFull
                key={itemsComparatore[i]?.pk || i}
                offerta={itemsComparatore[i]}
                showIi
                showIe
                onRemove={() => removeItem(itemsComparatore[i])}
                handleTracking={handleTracking}
              />
            ) : (
              <ComparatoreCard key={i} url={categoria.pagina.breadcrumbs?.[1]?.url || ''} handleTracking={handleTracking} />
            ) 
          )}
        </div>
      </WContainer>
      <div className={styles.whiteBox}>
        {itemsComparatore.length > 1 ? (
          <div ref={tableRef}>
            <ComparatoreTable
              className={styles.table}
              items={itemsComparatore}
              maxItems={maxItems}
              handleTracking={handleTracking}
            />
          </div>
        ) : (
          <ComparatoreSuggerimenti
            className={styles.suggerimenti}
            item={suggeritiMenu}
            addToCompare={addItem}
          />
        )}
      </div>
      {!!vantaggiMenu && (
        <VantaggiSection item={vantaggiMenu} color="grey" className={styles.vantaggi} />
      )}
      {!!helpBanner && <HelpBanner item={helpBanner} className={styles.helpBanner} />}
    </Page>
  )
}

export default ComparatoreTemplate
